import {
  Bars2Icon,
  ChevronRightIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon, IdentificationIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import { FC } from 'react'

import {
  formatAutoNumber,
  useAutos,
  useReorderAutos
} from '@/api/models/parking'
import { useRequestTypes } from '@/api/models/requests/hooks/useRequestTypes'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import EmptyListPlug from '@/components/pages/plugs/EmptyListPlug'
import SomethingWentWrong from '@/components/pages/plugs/SomethingWentWrong'
import { AppToast } from '@/components/shared/toast'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'

import { SetUserProfileRoute } from '../../../hooks/useUserProfileRoute'
import { UserProfileScreens } from '../../../lib/types'
import { FixedBottomDrawerButton } from '../../FixedBottomDrawerButton'
import { useSortableList } from '../hooks/useSortableList'
import { UserAutosListSkeleton } from './UserAutosSkeleton'

type Props = {
  savedToastMessage: string | null
  onClose: () => void
  setUserProfileRoute: SetUserProfileRoute
  onSavedAutoNumberShown: () => void
}

const UserAutosScreen: FC<Props> = ({
  savedToastMessage,
  onClose,
  setUserProfileRoute,
  onSavedAutoNumberShown
}) => {
  const { data: autos, isLoading, error } = useAutos()
  const { requestTypesByAlias } = useRequestTypes()
  const { mutate: reorderAutos } = useReorderAutos()
  const { reachGoal } = useYandexMetrika()

  const {
    sortableItems,
    draggingId,
    draggingOffset,
    registerItem,
    getHandleProps
  } = useSortableList({
    items: autos,
    getId: auto => auto.id,
    onReorder: nextAutos =>
      reorderAutos(nextAutos, {
        onSuccess: () => reachGoal(YandexMetrikaEvent.MY_AUTO_ORDER)
      })
  })

  const autoIconLink =
    requestTypesByAlias?.[RequestTypeAlias.GuestPassAuto]?.iconLink

  const handleGoToAuto = (autoId?: string) =>
    setUserProfileRoute(UserProfileScreens.Auto, autoId)

  const handleCardClick = (autoId: string) => {
    if (window.getSelection()?.toString()) {
      return
    }

    handleGoToAuto(autoId)
  }

  if (isLoading) {
    return <UserAutosListSkeleton />
  }

  if (error) {
    return (
      <>
        <div className="h-full pb-[120px]">
          <SomethingWentWrong withGoToMainButton={false} />
        </div>

        <FixedBottomDrawerButton text="Понятно" onClick={onClose} />
      </>
    )
  }

  if (autos.length === 0 && !isLoading) {
    return (
      <>
        <div className="pb-[120px]">
          <EmptyListPlug
            image={
              <div className="flex h-[124px] w-[124px] items-center justify-center rounded-full border border-grey-10 bg-white sm:h-100 sm:w-100">
                {autoIconLink ? (
                  <Image
                    src={autoIconLink}
                    alt=""
                    width={48}
                    height={48}
                    className="h-48 w-48 opacity-20 lg:h-36 lg:w-36"
                  />
                ) : (
                  <TruckIcon className="h-48 w-48 text-grey-20 lg:h-36 lg:w-36" />
                )}
              </div>
            }
            title="Транспорта нет"
            description={
              <span className="block max-w-[400px] text-18 sm:text-14">
                Добавьте транспорт, чтобы легко оформлять пропуска не вводя
                госномер
              </span>
            }
            className="mt-90"
          />
        </div>

        <FixedBottomDrawerButton
          text="Добавить транспорт"
          onClick={() => handleGoToAuto()}
        />
      </>
    )
  }

  return (
    <>
      <div className="mb-[120px] overflow-y-auto px-40 pb-40 pt-16 lg:px-24 lg:pb-24 lg:pt-0 sm:px-16 sm:pb-20">
        <div
          className={cn(
            'flex flex-col gap-12 sm:gap-8',
            draggingId && 'select-none'
          )}
        >
          {sortableItems.map(
            ({ id, emoji, number, region, model, description }) => {
              const isDragging = draggingId === id

              return (
                <div
                  key={id}
                  ref={registerItem(id)}
                  className={cn(
                    'flex w-full items-start gap-12 rounded-12 bg-white px-16 py-20 sm:px-8 sm:py-12',
                    isDragging &&
                      'relative z-10 shadow-[0_8px_24px_rgba(0,0,0,0.12)]'
                  )}
                  style={
                    isDragging
                      ? { transform: `translateY(${draggingOffset}px)` }
                      : undefined
                  }
                >
                  <button
                    type="button"
                    aria-label="Переместить"
                    className="mt-2 shrink-0 cursor-grab touch-none active:cursor-grabbing"
                    {...getHandleProps(id)}
                  >
                    <Bars2Icon className="h-24 w-24 text-grey-40" />
                  </button>

                  <button
                    type="button"
                    className="flex min-w-0 flex-1 cursor-pointer select-text flex-col gap-8 text-left"
                    onClick={() => handleCardClick(id)}
                  >
                    <div className="flex w-full items-center gap-8">
                      {emoji ? (
                        <span className="shrink-0 leading-none text-24 sm:text-20">
                          {emoji}
                        </span>
                      ) : (
                        <IdentificationIcon className="h-24 w-24 shrink-0 text-grey-50" />
                      )}

                      <p className="shrink-0 font-bold text-24 sm:text-20">
                        {formatAutoNumber(number, region)}
                      </p>

                      {model && (
                        <>
                          <span className="h-20 w-1 shrink-0 bg-grey-30" />
                          <p className="truncate text-grey-60 text-18 sm:text-16">
                            {model}
                          </p>
                        </>
                      )}

                      <ChevronRightIcon className="ml-auto h-24 w-24 shrink-0 text-grey-50" />
                    </div>

                    {description && (
                      <p className="line-clamp-2 font-pro text-grey-70 text-16 sm:text-14">
                        {description}
                      </p>
                    )}
                  </button>
                </div>
              )
            }
          )}
        </div>
      </div>
      <FixedBottomDrawerButton
        text="Добавить транспорт"
        onClick={() => handleGoToAuto()}
        topContent={
          savedToastMessage ? (
            <AppToast
              opened={Boolean(savedToastMessage)}
              message={savedToastMessage}
              onClose={onSavedAutoNumberShown}
              icon={<CheckCircleIcon className="h-20 w-20" />}
              duration={4000}
              className="bottom-[110px] right-40 lg:right-24 sm:right-16"
            />
          ) : null
        }
      />
    </>
  )
}

export { UserAutosScreen }
